import { useState, useRef, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuthStore } from '@/app/store/auth'
import { useObjectDocuments, useObjectVideos, useAddDocument, useAddVideo, useDeleteDocument, useDeleteVideo, useDocumentLibrary, useVideoLibrary } from '../hooks'
import type { ObjectDocument, ObjectVideo } from '../api'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog'
import { Upload, FileText, Video, Trash2, Eye, Download, Library, X, Play, File, FileSpreadsheet, Presentation } from 'lucide-react'
import { formatDate } from '@/lib/dateUtils'
import { useCan } from '@/hooks/useCan'

interface RecoveryDocumentVideoViewTabProps {
    objectID?: string | null
    scenario?: string
}

function formatFileSize(bytes: number | null | undefined): string {
    if (!bytes) return ''
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`
}

function getDocIcon(mimeType?: string | null) {
    if (!mimeType) return <FileText className="h-5 w-5" />
    if (mimeType.includes('pdf')) return <FileText className="h-5 w-5 text-red-500" />
    if (mimeType.includes('spreadsheet') || mimeType.includes('excel') || mimeType.includes('csv')) return <FileSpreadsheet className="h-5 w-5 text-green-600" />
    if (mimeType.includes('presentation') || mimeType.includes('powerpoint')) return <Presentation className="h-5 w-5 text-orange-500" />
    if (mimeType.includes('word') || mimeType.includes('document')) return <FileText className="h-5 w-5 text-blue-500" />
    return <File className="h-5 w-5 text-muted-foreground" />
}

export function RecoveryDocumentVideoViewTab({ objectID, scenario }: RecoveryDocumentVideoViewTabProps) {
    const { t, i18n } = useTranslation()
    const { data: documents = [], isLoading: docsLoading } = useObjectDocuments(objectID || null, scenario)
    const { data: videos = [], isLoading: vidsLoading } = useObjectVideos(objectID || null, scenario)
    const addDocumentMutation = useAddDocument()
    const addVideoMutation = useAddVideo()
    const deleteDocumentMutation = useDeleteDocument()
    const deleteVideoMutation = useDeleteVideo()
    const { data: docLibrary = [], refetch: refetchDocLibrary } = useDocumentLibrary()
    const { data: vidLibrary = [], refetch: refetchVidLibrary } = useVideoLibrary()

    const docInputRef = useRef<HTMLInputElement>(null)
    const vidInputRef = useRef<HTMLInputElement>(null)
    // Attaching and removing documents and videos changes the object: objects.update. Viewing and
    // downloading them is for everyone who can open it.
    const canEdit = useCan('objects.update')
    const [docUploading, setDocUploading] = useState(false)
    const [vidUploading, setVidUploading] = useState(false)
    const [docLibraryOpen, setDocLibraryOpen] = useState(false)
    const [vidLibraryOpen, setVidLibraryOpen] = useState(false)
    const [viewerOpen, setViewerOpen] = useState(false)
    const [viewerUrl, setViewerUrl] = useState('')
    const [viewerType, setViewerType] = useState<'document' | 'video'>('document')
    const [viewerTitle, setViewerTitle] = useState('')

    const scenarioId = scenario ? parseInt(scenario) : null

    const handleUpload = useCallback(async (file: globalThis.File, type: 'document' | 'video') => {
        if (!objectID || !scenarioId) return
        const setter = type === 'document' ? setDocUploading : setVidUploading
        setter(true)
        try {
            const token = useAuthStore.getState().accessToken
            const formData = new FormData()
            formData.append('image', file)
            const res = await fetch(`/api/upload?type=${type}&subdirectory=${type}s`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
                body: formData
            })
            const data = await res.json()
            if (!data.success) throw new Error(data.error || 'Upload failed')

            const fileInfo = data.file || data.image
            const mutation = type === 'document' ? addDocumentMutation : addVideoMutation
            await mutation.mutateAsync({
                objectID,
                data: {
                    scenario: scenarioId,
                    filename: fileInfo.filename,
                    originalName: file.name,
                    fileSize: file.size,
                    mimeType: file.type,
                }
            })
        } catch (err) {
            console.error(`${type} upload failed:`, err)
        } finally {
            setter(false)
        }
    }, [objectID, scenarioId, addDocumentMutation, addVideoMutation])

    const handleDocFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0]
        if (file) handleUpload(file, 'document')
        e.target.value = ''
    }

    const handleVidFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0]
        if (file) handleUpload(file, 'video')
        e.target.value = ''
    }

    const handleDeleteDocument = async (doc: ObjectDocument) => {
        if (!objectID) return
        await deleteDocumentMutation.mutateAsync({ objectID, docId: doc.id })
    }

    const handleDeleteVideo = async (vid: ObjectVideo) => {
        if (!objectID) return
        await deleteVideoMutation.mutateAsync({ objectID, videoId: vid.id })
    }

    const handleSelectFromDocLibrary = async (doc: ObjectDocument) => {
        if (!objectID || !scenarioId) return
        await addDocumentMutation.mutateAsync({
            objectID,
            data: {
                scenario: scenarioId,
                filename: doc.filename,
                originalName: doc.originalName,
                fileSize: doc.fileSize || undefined,
                mimeType: doc.mimeType || undefined,
            }
        })
        setDocLibraryOpen(false)
    }

    const handleSelectFromVidLibrary = async (vid: ObjectVideo) => {
        if (!objectID || !scenarioId) return
        await addVideoMutation.mutateAsync({
            objectID,
            data: {
                scenario: scenarioId,
                filename: vid.filename,
                originalName: vid.originalName,
                fileSize: vid.fileSize || undefined,
                mimeType: vid.mimeType || undefined,
                duration: vid.duration || undefined,
            }
        })
        setVidLibraryOpen(false)
    }

    const openViewer = (url: string, type: 'document' | 'video', title: string) => {
        setViewerUrl(url)
        setViewerType(type)
        setViewerTitle(title)
        setViewerOpen(true)
    }

    return (
        <div className="space-y-6">
            {/* Documents Section */}
            <div className="space-y-3">
                <div className="flex items-center justify-between">
                    <h3 className="text-sm font-medium">{t('objects.recoveryDocuments')}</h3>
                    {canEdit && (
                        <div className="flex items-center gap-2">
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => { refetchDocLibrary(); setDocLibraryOpen(true) }}
                            >
                                <Library className="h-4 w-4 mr-1" />
                                {t('objects.library')}
                            </Button>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => docInputRef.current?.click()}
                                disabled={docUploading}
                            >
                                <Upload className="h-4 w-4 mr-1" />
                                {docUploading ? t('common.uploading') : t('objects.uploadDocument')}
                            </Button>
                            <input
                                ref={docInputRef}
                                type="file"
                                className="hidden"
                                accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv"
                                onChange={handleDocFileChange}
                            />
                        </div>
                    )}
                </div>

                {docsLoading ? (
                    <div className="text-sm text-muted-foreground">{t('common.loading')}</div>
                ) : documents.length === 0 ? (
                    <div className="border-2 border-dashed border-muted-foreground/25 rounded-lg p-8 text-center">
                        <FileText className="h-10 w-10 mx-auto text-muted-foreground/50 mb-2" />
                        <p className="text-muted-foreground">{t('objects.noDocuments')}</p>
                        <p className="text-sm text-muted-foreground mt-1">{t('objects.noDocumentsDescription')}</p>
                    </div>
                ) : (
                    <div className="space-y-2">
                        {documents.map((doc) => (
                            <div key={doc.id} className="flex items-center justify-between border rounded-lg px-4 py-3 hover:bg-muted/50 transition-colors">
                                <div className="flex items-center gap-3 min-w-0">
                                    {getDocIcon(doc.mimeType)}
                                    <div className="min-w-0">
                                        <p className="text-sm font-medium truncate">{doc.originalName}</p>
                                        <p className="text-xs text-muted-foreground">
                                            {formatFileSize(doc.fileSize)}
                                            {doc.uploadedAt && ` \u2022 ${formatDate(doc.uploadedAt, i18n.language)}`}
                                        </p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-1">
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => openViewer(doc.url, 'document', doc.originalName)}
                                        title={t('common.view')}
                                    >
                                        <Eye className="h-4 w-4" />
                                    </Button>
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        asChild
                                        title={t('common.download')}
                                    >
                                        <a href={doc.url} download={doc.originalName}>
                                            <Download className="h-4 w-4" />
                                        </a>
                                    </Button>
                                    {canEdit && (
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => handleDeleteDocument(doc)}
                                            className="text-destructive hover:text-destructive"
                                            title={t('common.delete')}
                                        >
                                            <Trash2 className="h-4 w-4" />
                                        </Button>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Videos Section */}
            <div className="space-y-3">
                <div className="flex items-center justify-between">
                    <h3 className="text-sm font-medium">{t('objects.recoveryVideos')}</h3>
                    {canEdit && (
                        <div className="flex items-center gap-2">
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => { refetchVidLibrary(); setVidLibraryOpen(true) }}
                            >
                                <Library className="h-4 w-4 mr-1" />
                                {t('objects.library')}
                            </Button>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => vidInputRef.current?.click()}
                                disabled={vidUploading}
                            >
                                <Upload className="h-4 w-4 mr-1" />
                                {vidUploading ? t('common.uploading') : t('objects.uploadVideo')}
                            </Button>
                            <input
                                ref={vidInputRef}
                                type="file"
                                className="hidden"
                                accept="video/mp4,video/webm,video/ogg,video/quicktime,.mp4,.webm,.ogg,.mov,.avi"
                                onChange={handleVidFileChange}
                            />
                        </div>
                    )}
                </div>

                {vidsLoading ? (
                    <div className="text-sm text-muted-foreground">{t('common.loading')}</div>
                ) : videos.length === 0 ? (
                    <div className="border-2 border-dashed border-muted-foreground/25 rounded-lg p-8 text-center">
                        <Video className="h-10 w-10 mx-auto text-muted-foreground/50 mb-2" />
                        <p className="text-muted-foreground">{t('objects.noVideos')}</p>
                        <p className="text-sm text-muted-foreground mt-1">{t('objects.noVideosDescription')}</p>
                    </div>
                ) : (
                    <div className="space-y-2">
                        {videos.map((vid) => (
                            <div key={vid.id} className="flex items-center justify-between border rounded-lg px-4 py-3 hover:bg-muted/50 transition-colors">
                                <div className="flex items-center gap-3 min-w-0">
                                    <Video className="h-5 w-5 text-blue-500 flex-shrink-0" />
                                    <div className="min-w-0">
                                        <p className="text-sm font-medium truncate">{vid.originalName}</p>
                                        <p className="text-xs text-muted-foreground">
                                            {formatFileSize(vid.fileSize)}
                                            {vid.uploadedAt && ` \u2022 ${formatDate(vid.uploadedAt, i18n.language)}`}
                                        </p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-1">
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => openViewer(vid.url, 'video', vid.originalName)}
                                        title={t('common.play')}
                                    >
                                        <Play className="h-4 w-4" />
                                    </Button>
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        asChild
                                        title={t('common.download')}
                                    >
                                        <a href={vid.downloadUrl || vid.url} download={vid.originalName}>
                                            <Download className="h-4 w-4" />
                                        </a>
                                    </Button>
                                    {canEdit && (
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => handleDeleteVideo(vid)}
                                            className="text-destructive hover:text-destructive"
                                            title={t('common.delete')}
                                        >
                                            <Trash2 className="h-4 w-4" />
                                        </Button>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Document Library Dialog */}
            <Dialog open={docLibraryOpen} onOpenChange={setDocLibraryOpen}>
                <DialogContent className="max-w-lg max-h-[70vh]">
                    <DialogHeader>
                        <DialogTitle>{t('objects.documentLibrary')}</DialogTitle>
                        <DialogDescription>{t('objects.documentLibraryDescription')}</DialogDescription>
                    </DialogHeader>
                    <div className="overflow-y-auto max-h-[50vh] space-y-2">
                        {docLibrary.length === 0 ? (
                            <p className="text-sm text-muted-foreground text-center py-8">{t('objects.noDocumentsInLibrary')}</p>
                        ) : (
                            docLibrary.map((doc) => (
                                <div
                                    key={doc.id}
                                    className="flex items-center justify-between border rounded-lg px-4 py-3 hover:bg-muted/50 cursor-pointer transition-colors"
                                    onClick={() => handleSelectFromDocLibrary(doc)}
                                >
                                    <div className="flex items-center gap-3 min-w-0">
                                        {getDocIcon(doc.mimeType)}
                                        <div className="min-w-0">
                                            <p className="text-sm font-medium truncate">{doc.originalName}</p>
                                            <p className="text-xs text-muted-foreground">{formatFileSize(doc.fileSize)}</p>
                                        </div>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </DialogContent>
            </Dialog>

            {/* Video Library Dialog */}
            <Dialog open={vidLibraryOpen} onOpenChange={setVidLibraryOpen}>
                <DialogContent className="max-w-lg max-h-[70vh]">
                    <DialogHeader>
                        <DialogTitle>{t('objects.videoLibrary')}</DialogTitle>
                        <DialogDescription>{t('objects.videoLibraryDescription')}</DialogDescription>
                    </DialogHeader>
                    <div className="overflow-y-auto max-h-[50vh] space-y-2">
                        {vidLibrary.length === 0 ? (
                            <p className="text-sm text-muted-foreground text-center py-8">{t('objects.noVideosInLibrary')}</p>
                        ) : (
                            vidLibrary.map((vid) => (
                                <div
                                    key={vid.id}
                                    className="flex items-center justify-between border rounded-lg px-4 py-3 hover:bg-muted/50 cursor-pointer transition-colors"
                                    onClick={() => handleSelectFromVidLibrary(vid)}
                                >
                                    <div className="flex items-center gap-3 min-w-0">
                                        <Video className="h-5 w-5 text-blue-500 flex-shrink-0" />
                                        <div className="min-w-0">
                                            <p className="text-sm font-medium truncate">{vid.originalName}</p>
                                            <p className="text-xs text-muted-foreground">{formatFileSize(vid.fileSize)}</p>
                                        </div>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </DialogContent>
            </Dialog>

            {/* Document/Video Viewer Dialog */}
            <Dialog open={viewerOpen} onOpenChange={setViewerOpen}>
                <DialogContent className="max-w-4xl max-h-[90vh]">
                    <DialogHeader>
                        <DialogTitle className="truncate pr-8">{viewerTitle}</DialogTitle>
                        <DialogDescription>{viewerType === 'video' ? t('objects.videoPlayer') : t('objects.documentViewer')}</DialogDescription>
                    </DialogHeader>
                    <div className="w-full" style={{ minHeight: '60vh' }}>
                        {viewerType === 'video' ? (
                            <video
                                controls
                                autoPlay
                                className="w-full rounded-lg"
                                style={{ maxHeight: '70vh' }}
                                src={viewerUrl}
                            >
                                {t('objects.videoNotSupported')}
                            </video>
                        ) : (
                            <iframe
                                src={viewerUrl}
                                className="w-full rounded-lg border-0"
                                style={{ height: '70vh' }}
                                title={viewerTitle}
                            />
                        )}
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    )
}
